import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import './AlbumGallery.scss';
import { AppUser } from 'Src/views/App/App';
import { Photo } from 'Src/models/photo';
import { Video } from 'Src/models/video';
import Hls from 'hls.js';
import { ACCESS_PRESET_TYPE } from 'Src/types/album';
import { ROLE } from 'Src/consts/user';
import { PhotoEdit } from 'Src/views/GenericView/AdminView/AdminConsole/SchoolGallery/PhotoEdit/PhotoEdit';
import { TrashIcon } from 'Src/components/SvgIcons/TrashIcon';

type LightboxKind = { type: 'photo'; id: string } | { type: 'video'; id: string } | null;

interface Props {
  user?: AppUser;
  albumId?: string;
  photos?: Photo[];
  videos?: Video[];

  showDelete?: boolean;
  showEmptySections?: boolean;
  onDeletePhotoClick?: (photoId: string) => void;
  onDeleteVideoClick?: (videoId: string) => void;

  onAddPhotoClick?: () => void;
  onAddVideoClick?: () => void;

  isUpdatingAccessPreset?: boolean;
  onPhotoAccessPresetChange?: (photoId: string, preset: ACCESS_PRESET_TYPE) => void;
  onVideoAccessPresetChange?: (videoId: string, preset: ACCESS_PRESET_TYPE) => void;

  onUpdatePhotoMeta?: (photoId: string, patch: { name?: string; description?: string }) => void;
  onUpdateVideoMeta?: (videoId: string, patch: { name?: string; description?: string }) => void;
}

const Portal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);

  if (!hostRef.current) {
    hostRef.current = document.createElement('div');
  }

  useEffect(() => {
    const el = hostRef.current!;
    document.body.appendChild(el);
    return () => {
      document.body.removeChild(el);
    };
  }, []);

  return ReactDOM.createPortal(children, hostRef.current!);
};

const getStreamUrl = (v?: Video): string => (v && Array.isArray(v.streamData) && v.streamData[0]?.url) || '';

function useHls(
  videoRef: React.RefObject<HTMLVideoElement>,
  src: string,
  opts: { previewFirstFrame?: boolean; autoPlay?: boolean } = {}
) {
  const { previewFirstFrame = false, autoPlay = false } = opts;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !src) return;

    let hls: Hls | null = null;
    let touched = false;

    const playOnce = async () => {
      try {
        await el.play();
        touched = true;
        if (previewFirstFrame) {
          setTimeout(() => {
            try {
              el.pause();
            } catch {}
          }, 120);
        }
      } catch {}
    };

    const startPlayback = () => {
      if (autoPlay || previewFirstFrame) playOnce();
    };

    if (Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(el);
      const onParsed = () => startPlayback();
      hls.on(Hls.Events.MANIFEST_PARSED, onParsed);

      return () => {
        hls?.off(Hls.Events.MANIFEST_PARSED, onParsed);
        hls?.destroy();
        if (touched) {
          el.removeAttribute('src');
          try {
            el.load();
          } catch {}
        }
      };
    }

    if (el.canPlayType('application/vnd.apple.mpegurl') || el.canPlayType('application/x-mpegURL')) {
      el.src = src;
      startPlayback();
      return () => {
        if (touched) {
          el.removeAttribute('src');
          try {
            el.load();
          } catch {}
        }
      };
    }

    el.src = src;
    startPlayback();
    return () => {
      if (touched) {
        el.removeAttribute('src');
        try {
          el.load();
        } catch {}
      }
    };
  }, [videoRef, src, previewFirstFrame, autoPlay]);
}

const AlbPreviewVideo: React.FC<{ url: string }> = ({ url }) => {
  const ref = React.useRef<HTMLVideoElement | null>(null);

  React.useEffect(() => {
    if (!url || !ref.current) return;
    const el = ref.current;
    let hls: Hls | null = null;
    let played = false;

    const showFirstFrame = async () => {
      try {
        await el.play();
        played = true;
        setTimeout(() => el.pause(), 120);
      } catch {}
    };

    if (Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(el);
      hls.on(Hls.Events.MANIFEST_PARSED, showFirstFrame);
    } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
      el.src = url;
      showFirstFrame();
    } else {
      el.src = url;
      showFirstFrame();
    }

    return () => {
      if (hls) hls.destroy();
      if (played) {
        el.removeAttribute('src');
        try {
          el.load();
        } catch {}
      }
    };
  }, [url]);

  return (
    <video
      ref={ref}
      muted
      playsInline
      preload="metadata"
      style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
    />
  );
};

const LightboxVideo: React.FC<{ url: string }> = ({ url }) => {
  const ref = useRef<HTMLVideoElement | null>(null);
  useHls(ref, url, { autoPlay: true });
  return <video className="albLightbox__video" ref={ref} controls autoPlay playsInline />;
};

const AlbAccessPresets: React.FC<{
  value: ACCESS_PRESET_TYPE;
  onChange: (p: ACCESS_PRESET_TYPE) => void;
  disabled?: boolean;
  name?: string;
  label?: string;
}> = ({ value, onChange, disabled, name, label = 'Access preset' }) => {
  const groupName = React.useMemo(
    () =>
      name ||
      `alb-access-${Math.random()
        .toString(36)
        .slice(2)}`,
    [name]
  );
  const legendId = React.useMemo(
    () =>
      `alb-access-legend-${Math.random()
        .toString(36)
        .slice(2)}`,
    []
  );

  return (
    <fieldset className="albAccessPreset" aria-labelledby={legendId}>
      <legend id={legendId} className="albAside__label">
        {label}
      </legend>

      <div className="albAccessPreset__radios">
        <label>
          <input
            type="radio"
            name={groupName}
            checked={value === 'PRIVATE'}
            onChange={() => onChange('PRIVATE')}
            disabled={disabled}
          />
          Private
        </label>

        <label>
          <input
            type="radio"
            name={groupName}
            checked={value === 'ALL_PARTICIPANT_PARENTS'}
            onChange={() => onChange('ALL_PARTICIPANT_PARENTS')}
            disabled={disabled}
          />
          Staff/Student/Parents
        </label>
      </div>
    </fieldset>
  );
};

const canEditAccess = (user?: AppUser, media?: { author?: { schoolId?: string } }) => {
  if (!user) return false;
  if (user.activeRole === ROLE.STUDENT || user.activeRole === ROLE.PARENT) return false;
  const authorSchoolId = media?.author?.schoolId;
  return !authorSchoolId || authorSchoolId === user.activeSchoolId;
};

export const AlbumGallery: React.FC<Props> = ({
  user,
  albumId,
  photos = [],
  videos = [],
  showDelete = true,
  showEmptySections = true,
  onDeletePhotoClick,
  onDeleteVideoClick,
  onAddPhotoClick,
  onAddVideoClick,
  isUpdatingAccessPreset,
  onPhotoAccessPresetChange,
  onVideoAccessPresetChange,
  onUpdatePhotoMeta,
  onUpdateVideoMeta
}) => {
  const [lightbox, setLightbox] = useState<LightboxKind>(null);

  const photoList = useMemo(() => (photos || []).filter(Boolean), [photos]);
  const videoList = useMemo(() => (videos || []).filter(Boolean), [videos]);

  const openPhoto = useCallback((id: string) => setLightbox({ type: 'photo', id }), []);
  const openVideo = useCallback((id: string) => setLightbox({ type: 'video', id }), []);
  const closeLb = useCallback(() => setLightbox(null), []);

  const currentIndex = useMemo(() => {
    if (!lightbox) return -1;
    if (lightbox.type === 'photo') return photoList.findIndex(p => p.id === lightbox.id);
    return videoList.findIndex(v => v.id === lightbox.id);
  }, [lightbox, photoList, videoList]);

  const showPrev = useCallback(() => {
    if (!lightbox) return;
    if (lightbox.type === 'photo' && photoList.length) {
      const i = currentIndex <= 0 ? photoList.length - 1 : currentIndex - 1;
      setLightbox({ type: 'photo', id: photoList[i].id });
    }
    if (lightbox.type === 'video' && videoList.length) {
      const i = currentIndex <= 0 ? videoList.length - 1 : currentIndex - 1;
      setLightbox({ type: 'video', id: videoList[i].id });
    }
  }, [lightbox, currentIndex, photoList, videoList]);

  const showNext = useCallback(() => {
    if (!lightbox) return;
    if (lightbox.type === 'photo' && photoList.length) {
      const i = currentIndex >= photoList.length - 1 ? 0 : currentIndex + 1;
      setLightbox({ type: 'photo', id: photoList[i].id });
    }
    if (lightbox.type === 'video' && videoList.length) {
      const i = currentIndex >= videoList.length - 1 ? 0 : currentIndex + 1;
      setLightbox({ type: 'video', id: videoList[i].id });
    }
  }, [lightbox, currentIndex, photoList, videoList]);

  const isPlayable = (v: Video) =>
    v?.status === 'COMPLETE' && Array.isArray(v?.streamData) && v.streamData.length > 0 && !!v.streamData[0]?.url;

  const renderLightbox = () => {
    if (!lightbox) return null;

    const isPhoto = lightbox.type === 'photo';
    const photo = isPhoto ? photoList.find(p => p.id === lightbox.id) : undefined;
    const video = !isPhoto ? videoList.find(v => v.id === lightbox.id) : undefined;
    if ((isPhoto && !photo) || (!isPhoto && !video)) return null;

    const canEditThis = isPhoto ? canEditAccess(user, photo) : canEditAccess(user, video);
    const url = !isPhoto ? getStreamUrl(video) : '';

    return (
      <Portal>
        <div className="albLightbox" onClick={closeLb}>
          <div
            className={`albLightbox__inner ${canEditThis ? 'albLightbox__inner--aside' : ''}`}
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="albLightbox__stage">
              {isPhoto ? (
                <>
                  <img src={photo!.picUrl} alt={photo!.name || 'Photo'} className="albLightbox__image" />

                  {(photo?.name || photo?.description) && (
                    <div className="albLightbox__captionRibbon" role="note">
                      {photo?.name && <div className="title">{photo.name}</div>}
                      {photo?.description && <div className="desc">{photo.description}</div>}
                    </div>
                  )}
                </>
              ) : (
                <LightboxVideo url={url} />
              )}

              <button className="albLightbox__nav left" onClick={showPrev} aria-label="Previous" />
              <button className="albLightbox__nav right" onClick={showNext} aria-label="Next" />
              <button className="albLightbox__close" onClick={closeLb} aria-label="Close" />
            </div>

            {canEditThis && (
              <aside className="albLightbox__aside">
                <div className="eEditPhotoPanel">
                  <span
                    className="bTooltip"
                    id="albumDelete_button"
                    data-description={isPhoto ? 'Delete photo' : 'Delete video'}
                    onClick={() => (isPhoto ? onDeletePhotoClick?.(photo!.id) : onDeleteVideoClick?.(video!.id))}
                  >
                    <TrashIcon />
                  </span>
                </div>

                {isPhoto && (
                  <PhotoEdit
                    key={photo!.id}
                    albumId={albumId}
                    photo={photo!}
                    onSubmit={(id, info) => onUpdatePhotoMeta?.(id, info)}
                  />
                )}

                {isPhoto ? (
                  <AlbAccessPresets
                    name={`alb-access-${photo!.id}`}
                    value={photo!.accessPreset as ACCESS_PRESET_TYPE}
                    onChange={p => onPhotoAccessPresetChange?.(photo!.id, p)}
                    disabled={isUpdatingAccessPreset}
                  />
                ) : (
                  <AlbAccessPresets
                    name={`alb-access-${video!.id}`}
                    value={video!.accessPreset as ACCESS_PRESET_TYPE}
                    onChange={p => onVideoAccessPresetChange?.(video!.id, p)}
                    disabled={isUpdatingAccessPreset}
                  />
                )}
              </aside>
            )}
          </div>
        </div>
      </Portal>
    );
  };

  return (
    <div className="albGallery">
      {(onAddPhotoClick || onAddVideoClick) && (
        <div className="albGallery__toolbar">
          {onAddPhotoClick && (
            <button type="button" className="btn btn-primary mr-2" onClick={onAddPhotoClick}>
              Add photo
            </button>
          )}
          {onAddVideoClick && (
            <button type="button" className="btn btn-primary" onClick={onAddVideoClick}>
              Add video
            </button>
          )}
        </div>
      )}

      {photoList.length > 0 && <h3 className="mt-3">Photos</h3>}
      {photoList.length > 0 ? (
        <div className="albStrip">
          {photoList.map(p => (
            <div key={p.id} className="albThumb" title={p.name || ''}>
              {showDelete && onDeletePhotoClick && (
                <button
                  className="albThumb__delete"
                  onClick={e => {
                    e.stopPropagation();
                    onDeletePhotoClick(p.id);
                  }}
                  aria-label="Delete photo"
                  title="Delete photo"
                  type="button"
                />
              )}
              <div className="albThumb__imgWrap" onClick={() => openPhoto(p.id)}>
                <img src={p.picUrl} alt={p.name || 'Photo'} />
              </div>
            </div>
          ))}
        </div>
      ) : showEmptySections ? (
        <p className="text-muted">No photos yet.</p>
      ) : null}

      {videoList.length > 0 && <h3 className="mt-4">Videos</h3>}
      {videoList.length > 0 ? (
        <div className="albGrid">
          {videoList.map(v => {
            const playable = isPlayable(v as any);
            const streamUrl = playable ? v.streamData && v.streamData[0]?.url : '';
            const isLocal = (v as any).isLocal === true;
            const phase = isLocal ? (v as any).status : playable ? 'READY' : 'PROCESSING';
            const statusLabel = phase === 'UPLOADING' ? 'Uploading…' : phase === 'PROCESSING' ? 'Processing…' : '';

            return (
              <div key={v.id} className="albVideoCard">
                <div
                  className="albVideoCard__poster"
                  onClick={playable ? () => openVideo(v.id) : undefined}
                  title={playable ? v.fileName || '' : statusLabel}
                  role={playable ? 'button' : undefined}
                  tabIndex={playable ? 0 : -1}
                  onKeyDown={
                    playable
                      ? e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openVideo(v.id);
                          }
                        }
                      : undefined
                  }
                >
                  {playable ? <AlbPreviewVideo url={streamUrl} /> : null}

                  {phase === 'UPLOADING' && <span className="albBadge albBadge--uploading">Uploading…</span>}
                  {phase === 'PROCESSING' && <span className="albBadge albBadge--processing">Processing…</span>}

                  <div className="albVideoCard__play" />
                </div>

                <div className="albVideoCard__meta">
                  <div className="name" title={v.fileName || ''}>
                    {v.fileName || 'Video'}
                  </div>
                </div>

                {showDelete && onDeleteVideoClick && (
                  <button
                    className="albVideoCard__delete"
                    onClick={() => onDeleteVideoClick(v.id)}
                    title="Delete video"
                    aria-label="Delete video"
                    type="button"
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : showEmptySections ? (
        <p className="text-muted">No videos yet.</p>
      ) : null}

      {renderLightbox()}
    </div>
  );
};

